"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReferenceModule as ResourceReferences } from "@/sanity/sanity.types";
import { PortableText } from "next-sanity";
import { useEffect, useState } from "react";
import Accordion, { AccordionStyle } from "../../common/accordion/accordion";
import BaseLink from "../../common/base-link/base-link";
import fileUrl from "@/sanity/utilities/fileUrl";
import Image from "../../common/image/image";
interface ReferenceModuleProps {
  data: ResourceReferences;
}
const getDomain = (url?: string) => {
  if (!url) return undefined;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
};
const ResourceReferences = t0 => {
  const $ = _c(28);
  const {
    data
  } = t0;
  const {
    heading,
    items: t1
  } = data;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const items = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [fileContents, setFileContents] = useState(t3);
  let t4;
  if ($[3] !== data.file) {
    t4 = data.file?.asset ? fileUrl(data.file.asset as any) : "";
    $[3] = data.file;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const fileAssetUrl = t4;
  let t5;
  let t6;
  if ($[5] !== fileAssetUrl) {
    t5 = () => {
      if (!fileAssetUrl) {
        setFileContents([]);
        return;
      }
      fetch(fileAssetUrl).then(_temp).then(text => {
        const numbers = text.split("\n").map(_temp2).filter(Boolean);
        setFileContents(numbers);
      });
    };
    t6 = [fileAssetUrl];
    $[5] = fileAssetUrl;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  const hasItems = items.length > 0;
  const hasFileContents = fileContents.length > 0;
  if (!hasItems && !hasFileContents) {
    return null;
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Image src="/book-icon.svg" alt="" width={20} height={20} className="shrink-0" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = heading ?? "References";
  let t9;
  if ($[9] !== t8) {
    t9 = <span className="font-lora text-xl font-semibold text-amc-gray-hl-primary">{t8}</span>;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== hasItems || $[12] !== items.length) {
    t10 = hasItems && <span className="flex h-6 shrink-0 items-center rounded-md bg-[#EEF7FA] px-2.5 text-xs font-semibold text-amc-gray-body-primary">{items.length}<span className="ml-1">{items.length === 1 ? "source" : "sources"}</span></span>;
    $[11] = hasItems;
    $[12] = items.length;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <>{t7}{t9}{t10}</>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== hasItems || $[18] !== items) {
    t12 = hasItems && <ol>{items.map(_temp3)}</ol>;
    $[17] = hasItems;
    $[18] = items;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== fileContents || $[21] !== hasFileContents || $[22] !== hasItems) {
    t13 = hasFileContents && <ul className={`list-disc px-10 py-4 ${hasItems ? "border-t border-[#E3E8EC]" : ""}`}>{fileContents.map(_temp4)}</ul>;
    $[20] = fileContents;
    $[21] = hasFileContents;
    $[22] = hasItems;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t12 || $[26] !== t13) {
    t14 = <section id="referenceModule" className="w-full font-work-sans"><Accordion accordionStyle={AccordionStyle.PLAIN} showToggle={true} className="overflow-hidden rounded-lg border border-[#D1D5DB] bg-white" titleClassName="flex w-full items-center gap-3 px-6 py-5 text-left" panelClassName="border-t border-[#E3E8EC]" title={t11}>{t12}{t13}</Accordion></section>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
};
export default ResourceReferences;
function _temp(res) {
  return res.text();
}
function _temp2(line) {
  return line.trim();
}
function _temp3(item, index) {
  const domain = getDomain(item.url);
  const year = item.date ? item.date.slice(0, 4) : undefined;
  return <li key={item._key} className="flex gap-3 px-6 py-4 transition-colors duration-150 ease-out hover:bg-[#F2F8FB] [&+li]:border-t [&+li]:border-[#E3E8EC]"><span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00736E] text-xs font-bold text-white">{index + 1}</span><div>{item.title && <div className="text-base font-semibold leading-snug text-amc-gray-hl-primary"><PortableText value={item.title} /></div>}{(item?.subheading || domain) && <p className="mt-1 text-sm text-amc-gray-hl-800">{[item.subheading, year].filter(Boolean).join(" \xB7 ")}{(item.subheading || year) && domain && " \xB7 "}{domain && <BaseLink href={item.url ?? ""} target="_blank" rel="noopener noreferrer"><span className="inline-flex items-center gap-1 text-amc-blue-primary underline">{domain}<Image src="/external-row.svg" alt="" width={13} height={13} /></span></BaseLink>}</p>}</div></li>;
}
function _temp4(reference, idx) {
  return <li key={idx}>{reference}</li>;
}